import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type SimpleMagnifierProps = {
  src: string; // displayed image
  zoomSrc?: string; // hi-res image for zoom, defaults to src
  width: number; // rendered width (px)
  height: number; // rendered height (px)
  zoomWidth?: number; // width of the zoom window (px)
  lensSize?: number; // square lens size (px)
  zoomPosition?: 'right' | 'left' | 'original';
  className?: string;
};

export default function SimpleMagnifier({
  src,
  zoomSrc,
  width,
  height,
  zoomWidth = 500,
  lensSize = 100,
  zoomPosition = 'right',
  className,
}: SimpleMagnifierProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [active, setActive] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 }); // lens top-left
  const [zoomDims, setZoomDims] = useState({ w: 0, h: 0 });
  const [zoomPlacement, setZoomPlacement] = useState<{
    top: number;
    left: number;
    zIndex: number;
  }>();

  const hiSrc = zoomSrc || src;

  useEffect(() => setMounted(true), []);

  // Preload zoom image to get natural size
  useEffect(() => {
    const img = new Image();
    img.onload = () =>
      setZoomDims({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = hiSrc;
  }, [hiSrc]);

  const clamp = (v: number, min: number, max: number) =>
    Math.max(min, Math.min(v, max));

  const resolveZIndex = (start: HTMLElement) => {
    if (typeof window === 'undefined') {
      return 1;
    }

    let current: HTMLElement | null = start;
    while (current) {
      const value = window.getComputedStyle(current).zIndex;
      const parsed = Number.parseInt(value, 10);
      if (!Number.isNaN(parsed)) {
        return parsed + 1;
      }
      current = current.parentElement;
    }

    return 9999;
  };

  const updateFromClientPoint = (clientX: number, clientY: number) => {
    const el = wrapRef.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const x = clamp(clientX - rect.left - lensSize / 2, 0, width - lensSize);
    const y = clamp(clientY - rect.top - lensSize / 2, 0, height - lensSize);
    setPos({ x, y });

    if (zoomPosition !== 'original' && typeof window !== 'undefined') {
      const zoomBoxHeight = (zoomWidth * height) / width;
      const spacing = 12;
      const top = clamp(
        rect.top,
        spacing,
        Math.max(window.innerHeight - zoomBoxHeight - spacing, spacing)
      );
      const desiredLeft =
        zoomPosition === 'right'
          ? rect.right + spacing
          : rect.left - zoomWidth - spacing;
      const left = clamp(
        desiredLeft,
        spacing,
        Math.max(window.innerWidth - zoomWidth - spacing, spacing)
      );
      const zIndex = resolveZIndex(el);
      setZoomPlacement({ top, left, zIndex });
    }
  };

  // Use pointer events for mouse + touch
  const onPointerEnter: React.PointerEventHandler<HTMLDivElement> = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setActive(true);
    updateFromClientPoint(e.clientX, e.clientY);
  };
  const onPointerMove: React.PointerEventHandler<HTMLDivElement> = (e) => {
    if (!active) return;
    updateFromClientPoint(e.clientX, e.clientY);
  };
  const onPointerLeave: React.PointerEventHandler<HTMLDivElement> = (e) => {
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    setActive(false);
    setZoomPlacement(undefined);
  };

  const cx = zoomDims.w / width || 1;
  const cy = zoomDims.h / height || 1;

  // Decide where to place the zoom window
  const zoomBoxStyle: React.CSSProperties =
    zoomPosition === 'original'
      ? {
          position: 'absolute',
          left: 0,
          top: 0,
          width,
          height,
        }
      : {
          width: zoomWidth,
          height: (zoomWidth * height) / width,
        };

  return (
    <div
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'flex-start',
        ...(zoomPosition === 'left' ? { flexDirection: 'row-reverse' } : {}),
      }}
    >
      <div
        ref={wrapRef}
        onPointerEnter={onPointerEnter}
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerLeave}
        style={{
          position: 'relative',
          width,
          height,
          // important: ensure the wrapper receives events; the <img> won't block them
          touchAction: 'none',
          userSelect: 'none',
          overflow: 'hidden',
        }}
      >
        {/* The visible image. pointerEvents: none lets the wrapper get all events */}
        <img
          src={src}
          alt=""
          draggable={false}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            pointerEvents: 'none',
          }}
        />

        {/* Lens */}
        {mounted && active && (
          <div
            style={{
              position: 'absolute',
              left: pos.x,
              top: pos.y,
              width: lensSize,
              height: lensSize,
              border: '1px solid rgba(0,0,0,.25)',
              background: 'rgba(255,255,255,.25)',
              backdropFilter: 'saturate(120%)',
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Overlaid zoom (if zoomPosition === 'original') */}
        {mounted && active && zoomPosition === 'original' && zoomDims.w > 0 && (
          <div
            style={{
              ...zoomBoxStyle,
              backgroundImage: `url(${hiSrc})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: `${zoomDims.w}px ${zoomDims.h}px`,
              backgroundPosition: `-${pos.x * cx}px -${pos.y * cy}px`,
              mixBlendMode: 'normal',
              pointerEvents: 'none',
            }}
          />
        )}
      </div>

      {/* Side zoom window */}
      {mounted &&
        active &&
        zoomPosition !== 'original' &&
        zoomDims.w > 0 &&
        zoomPlacement &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: zoomPlacement.top,
              left: zoomPlacement.left,
              ...zoomBoxStyle,
              backgroundImage: `url(${hiSrc})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: `${zoomDims.w}px ${zoomDims.h}px`,
              backgroundPosition: `-${pos.x * cx}px -${pos.y * cy}px`,
              boxShadow: '0 8px 20px rgba(0,0,0,.18)',
              pointerEvents: 'none',
              zIndex: zoomPlacement.zIndex,
              backgroundColor: '#fff'
            }}
          />,
          document.body
        )}
    </div>
  );
}
